import { useEffect, useMemo, useState } from 'react';
import {
    Box, Card, CardContent, Typography, Chip, Button, TextField, Snackbar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import {
    getMyReferredSales, getMyReferralLink, getMyReferralEnquiries,
} from '../../api/commissionProgressApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

/**
 * Referral Dashboard — what a referrer sees about their OWN introductions.
 * (user 2026-10-18: "they registered as referral in the form ald one. so now i wan to
 * give them dashboard to see their sales")
 *
 * A Referral-role user could previously see one menu item, Referral Fee, and it was
 * empty for them: every referral list in the app is keyed on the SELLING AGENT, and a
 * referrer is never one. This page reads GetMyReferredSales, which is keyed on
 * Sales.ReferralUserId instead — the same single derivation behind the All RF list and
 * the referral report, so the four screens cannot disagree about what a fee is.
 *
 * The loyalty tiers ("what to hit, what perks") are a separate, pending piece — the
 * thresholds and the perks are the owner's to set, and this page leaves room for them
 * rather than inventing numbers.
 */

const FONT = "'Poppins', sans-serif";

// Same flattening as the referral report, for the same reason: the awkward two-object
// API response is unpacked exactly once and everything below reads this shape.
function flatten(r) {
    const d = r.salesDetailsForCommissionProgress || {};
    const cp = r.commissionProgressDto || {};
    const fee = Number(cp.commPay) || 0;
    const paid = (Number(cp.firstPayment) || 0) + (Number(cp.secondPayment) || 0);
    return {
        salesId: d.salesId,
        customerName: d.customerName || '(unnamed)',
        agentName: d.agentName,
        finalPrice: Number(d.finalPrice) || 0,
        depositOn: d.depositOn,
        status: d.status,
        isArchived: !!d.isArchived,
        ratePct: d.referralRatePct,
        fee,
        paid,
        // Never negative: an overpayment is a different problem and must not quietly
        // cancel out an unpaid fee inside a total.
        outstanding: Math.max(0, fee - paid),
    };
}

const STATUS_COLOR = {
    Active: 'success',
    Pending: 'warning',
    Completed: 'success',
    'Dispute Pricing': 'error',
};

function Stat({ label, value, hint }) {
    return (
        <Card sx={{ flex: '1 1 180px', minWidth: 0, borderRadius: 2 }}>
            <CardContent>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.5rem', color: '#0F172A', mt: 0.5, wordBreak: 'break-word' }}>
                    {value}
                </Typography>
                {hint ? (
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: '#94A3B8', mt: 0.25 }}>
                        {hint}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
}

export default function ReferralDashboard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [link, setLink] = useState('');
    const [enquiries, setEnquiries] = useState([]);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let alive = true;
        // The code is minted on first ask, so simply opening this page gives a referrer
        // their link — there is no button to press before they have one.
        getMyReferralLink()
            .then(res => {
                const code = res?.data?.code;
                if (alive && code) setLink(`${window.location.origin}/r/${code}`);
            })
            .catch(() => { /* the page is still useful without it */ });
        getMyReferralEnquiries()
            .then(res => { if (alive) setEnquiries(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { /* ditto */ });
        return () => { alive = false; };
    }, []);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
        } catch {
            // Clipboard is blocked on an insecure origin and in some in-app browsers.
            // The field is selectable, so there is still a way through.
            setCopied(false);
        }
    };

    useEffect(() => {
        let alive = true;
        getMyReferredSales()
            .then(res => {
                if (!alive) return;
                const list = Array.isArray(res?.data) ? res.data : [];
                // Archived sales are out of the book entirely, so they are out of here too —
                // the same rule the referral report applies.
                setRows(list.map(flatten).filter(r => !r.isArchived));
            })
            .catch(() => { if (alive) setError('Could not load your referrals. Please try again.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const totals = useMemo(() => rows.reduce((acc, r) => ({
        count: acc.count + 1,
        earned: acc.earned + r.fee,
        paid: acc.paid + r.paid,
        outstanding: acc.outstanding + r.outstanding,
        sales: acc.sales + r.finalPrice,
    }), { count: 0, earned: 0, paid: 0, outstanding: 0, sales: 0 }), [rows]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.4rem', color: '#0F172A', mb: 0.5 }}>
                My Referrals
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B', mb: 2.5 }}>
                Everyone you have introduced, what their system came to, and what you have earned.
            </Typography>

            {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

            {link ? (
                <Card sx={{ borderRadius: 2, mb: 3, border: '1px solid #BBF7D0', background: '#F0FDF4' }}>
                    <CardContent>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
                            Your referral link
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#475569', mb: 1.5 }}>
                            Send this to anyone you think should go solar. Whoever fills it in comes
                            through as yours — you don’t have to tell anyone here.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                value={link}
                                InputProps={{ readOnly: true }}
                                onFocus={(e) => e.target.select()}
                                sx={{ flex: '1 1 260px', background: '#fff' }}
                            />
                            <Button variant="contained" onClick={copyLink}
                                sx={{ fontFamily: FONT, fontWeight: 700 }}>
                                Copy
                            </Button>
                            <Button
                                variant="outlined"
                                component="a"
                                href={`https://wa.me/?text=${encodeURIComponent(
                                    `Hi! I think solar would suit your place. Leave your details here and the team will work out what your roof could save you: ${link}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontFamily: FONT, fontWeight: 700 }}
                            >
                                Share on WhatsApp
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ) : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Stat label="Referrals" value={totals.count} hint={totals.count === 1 ? '1 introduction' : `${totals.count} introductions`} />
                <Stat label="Total earned" value={handlePriceWithRM(totals.earned)} />
                <Stat label="Paid to you" value={handlePriceWithRM(totals.paid)} />
                <Stat label="Still owed" value={handlePriceWithRM(totals.outstanding)} />
            </Box>

            {rows.length === 0 ? (
                <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                            No referrals yet
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B' }}>
                            When someone you introduced signs up, their sale appears here with
                            your fee, and you can follow it through to payment.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <TableContainer component={Card} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Customer', 'Date', 'Status', 'System value', 'Your fee', 'Paid', 'Still owed'].map(h => (
                                    <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.salesId} hover>
                                    <TableCell sx={{ fontFamily: FONT }}>{r.customerName}</TableCell>
                                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                                        {r.depositOn ? convertDateTimeToDateString(r.depositOn) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={r.status || '—'}
                                            color={STATUS_COLOR[r.status] || 'default'}
                                            sx={{ fontFamily: FONT, fontSize: '0.7rem' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>{handlePriceWithRM(r.finalPrice)}</TableCell>
                                    <TableCell sx={{ fontFamily: FONT, fontWeight: 600, whiteSpace: 'nowrap' }}>{handlePriceWithRM(r.fee)}</TableCell>
                                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>{handlePriceWithRM(r.paid)}</TableCell>
                                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap', color: r.outstanding > 0 ? '#B45309' : '#64748B' }}>
                                        {handlePriceWithRM(r.outstanding)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {enquiries.length > 0 ? (
                <Box sx={{ mt: 4 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem', color: '#0F172A', mb: 0.5 }}>
                        People who used your link
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#64748B', mb: 1.5 }}>
                        Introductions waiting to become sales. Once one does, it moves into the
                        table above with your fee on it.
                    </Typography>
                    <TableContainer component={Card} sx={{ borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Name', 'Phone', 'Where', 'Sent', 'Status'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {enquiries.map(e => (
                                    <TableRow key={e.referralEnquiryId} hover>
                                        <TableCell sx={{ fontFamily: FONT }}>{e.name}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>{e.phone}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT }}>{e.location || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                                            {e.createdOn ? convertDateTimeToDateString(e.createdOn) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small"
                                                label={e.salesId ? 'Became a sale' : 'With the team'}
                                                color={e.salesId ? 'success' : 'default'}
                                                sx={{ fontFamily: FONT, fontSize: '0.7rem' }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ) : null}

            <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: '#94A3B8', mt: 2 }}>
                Figures follow the sale, so a fee appears as soon as the sale does and moves to
                paid when Accounts pays it. Last loaded {dayjs().format('D MMM YYYY, h:mm a')}.
            </Typography>

            <Snackbar
                open={copied}
                autoHideDuration={2500}
                onClose={() => setCopied(false)}
                message="Link copied"
            />
        </Box>
    );
}
